import React, {useEffect, useRef, useState} from 'react';
import {View, Text, TouchableOpacity, StyleSheet} from 'react-native';
import I18n from 'i18n-js';
import CommonStyles from '../../style/commonStyles';
import CustomScrollBar from '../../commons/customScrollBar';
import useScanToCartWorkflowEnabled from '../../helpers/useScanToCartWorkflowEnabled';
import {
  fetchCartAssignmentStates,
  assignOrdersToCarts
} from '../../actions/scanToCartActions';

const STATE_LABEL_KEYS = {
  assigned: 'order.cartStateAssigned',
  pick_in_progress: 'order.cartStatePickInProgress',
  picked: 'order.cartStatePicked',
  scanned_not_cleared: 'order.cartStateScannedNotCleared'
};

const CartRow = ({cart, checked, onToggle}) => {
  const state = cart.assignment_state;
  const reasonKey = STATE_LABEL_KEYS[state];
  const busy = state !== 'empty';
  return (
    <TouchableOpacity
      style={[styles.cartRow, busy && styles.cartRowBusy]}
      testID={`cartAssignment_cart_${cart.cart_id}`}
      onPress={() => onToggle(cart.id)}>
      <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
        {checked && <Text style={styles.checkIcon}>✓</Text>}
      </View>
      <Text
        style={[styles.cartName, busy && styles.busyText]}
        numberOfLines={1}>
        {cart.cart_id}
      </Text>
      {reasonKey && (
        <Text
          style={[styles.reason, styles.busyText]}
          testID={`cartAssignment_reason_${cart.cart_id}`}>
          {I18n.t(reasonKey)}
        </Text>
      )}
    </TouchableOpacity>
  );
};

const OrderCartAssignment = ({getAssignRequest}) => {
  const scanToCartEnabled = useScanToCartWorkflowEnabled();
  const dropdownRef = useRef(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);
  const [isOpen, setIsOpen] = useState(false);
  const [carts, setCarts] = useState([]);
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [checkedIds, setCheckedIds] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');

  const loadCarts = async () => {
    const requestId = ++requestIdRef.current;
    setStatus('pending');
    const result = await fetchCartAssignmentStates();
    if (requestId !== requestIdRef.current) return;
    if (result.ok) {
      setCarts(result.data);
      setCheckedIds(prev =>
        prev.filter(id => result.data.some(cart => cart.id === id))
      );
      setStatus('succeeded');
    } else {
      setErrorMessage(String(result.error));
      setStatus('failed');
    }
  };

  const checkedCarts = carts.filter(cart => checkedIds.includes(cart.id));
  const canAssign =
    checkedCarts.length > 0 &&
    checkedCarts.every(cart => cart.assignment_state === 'empty');

  const assign = async () => {
    if (assigning) return;
    const request = getAssignRequest(checkedCarts.map(cart => cart.id));
    if (request.error) {
      setAssignError(request.error);
      return;
    }
    setAssignError('');
    setAssigning(true);
    const result = await assignOrdersToCarts(request.payload);
    if (!mountedRef.current) return;
    setAssigning(false);
    if (!result.ok) {
      setAssignError(String(result.error));
      return;
    }
    requestIdRef.current++;
    setCheckedIds([]);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (isOpen) {
      requestIdRef.current++;
      setIsOpen(false);
      return;
    }
    setIsOpen(true);
    loadCarts();
  };

  const toggleCart = id => {
    setAssignError('');
    setCheckedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        requestIdRef.current++;
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestIdRef.current++;
    };
  }, []);

  if (!scanToCartEnabled) return null;

  return (
    <View ref={dropdownRef}>
      <TouchableOpacity
        style={styles.button}
        testID="cartAssignmentToggle"
        onPress={toggleDropdown}>
        <View style={CommonStyles.buttonContent}>
          <Text style={{fontFamily: 'Poppins_600SemiBold', color: '#7a7a7a'}}>
            {I18n.t('order.cartAssignmentLabel')}
          </Text>
          <i
            className={`${!isOpen ? 'icon-down-open' : 'icon-up-open'}`}
            style={{color: '#7A7A7A', fontSize: 15}}></i>
        </View>
      </TouchableOpacity>
      {isOpen && (
        <View style={styles.dropDownContainer} testID="cartAssignmentPanel">
          {status === 'pending' && (
            <Text style={styles.message} testID="cartAssignmentLoading">
              {I18n.t('order.cartAssignmentLoading')}
            </Text>
          )}
          {status === 'failed' && (
            <View>
              <Text style={styles.errorText} testID="cartAssignmentError">
                {I18n.t('order.cartAssignmentLoadError', {
                  message: errorMessage
                })}
              </Text>
              <TouchableOpacity
                onPress={loadCarts}
                testID="cartAssignmentRetry"
                style={styles.retry}>
                <Text style={styles.retryText}>
                  {I18n.t('order.cartAssignmentRetry')}
                </Text>
              </TouchableOpacity>
            </View>
          )}
          {status === 'succeeded' && carts.length === 0 && (
            <Text style={styles.message} testID="cartAssignmentEmpty">
              {I18n.t('order.cartAssignmentNoCarts')}
            </Text>
          )}
          {status === 'succeeded' && canAssign && (
            <TouchableOpacity
              style={[styles.assignButton, assigning && styles.assignButtonBusy]}
              testID="cartAssignmentAssign"
              disabled={assigning}
              onPress={assign}>
              <Text style={styles.assignText}>
                {assigning
                  ? I18n.t('order.cartAssignmentAssigning')
                  : I18n.t('order.cartAssignmentAssign')}
              </Text>
            </TouchableOpacity>
          )}
          {!!assignError && (
            <Text style={styles.errorText} testID="cartAssignmentAssignError">
              {assignError}
            </Text>
          )}
          {status === 'succeeded' && carts.length > 0 && (
            <CustomScrollBar>
              <View style={styles.list}>
                {carts.map(cart => (
                  <CartRow
                    key={cart.id}
                    cart={cart}
                    checked={checkedIds.includes(cart.id)}
                    onToggle={toggleCart}
                  />
                ))}
              </View>
            </CustomScrollBar>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  button: {
    maxWidth: '107%',
    backgroundColor: '#262B30',
    marginLeft: 2
  },
  dropDownContainer: {
    position: 'absolute',
    top: '80%',
    left: 0,
    right: 0,
    borderRadius: 5,
    marginTop: 5,
    width: '15rem',
    maxHeight: '50vh',
    minWidth: '9vw',
    backgroundColor: 'rgb(38, 43, 48)',
    padding: 1
  },
  list: {marginTop: 10},
  cartRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8
  },
  cartRowBusy: {opacity: 0.55},
  checkbox: {
    width: 14,
    height: 14,
    borderWidth: 1,
    borderColor: '#9a9a9a',
    borderRadius: 2,
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center'
  },
  checkboxChecked: {backgroundColor: '#A97627', borderColor: '#A97627'},
  checkIcon: {color: '#fff', fontSize: 10, lineHeight: 12},
  cartName: {flex: 1, color: '#fff', fontFamily: 'Poppins_400Regular'},
  busyText: {color: '#9a9a9a'},
  reason: {fontSize: 11, marginLeft: 6, fontFamily: 'Poppins_400Regular'},
  message: {color: '#cfcfcf', padding: 10, textAlign: 'center'},
  errorText: {color: '#e57373', padding: 10},
  retry: {alignSelf: 'center', padding: 6},
  assignButton: {
    alignSelf: 'center',
    marginTop: 8,
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 3,
    backgroundColor: '#A97627'
  },
  assignButtonBusy: {opacity: 0.6},
  assignText: {color: '#fff', fontFamily: 'Poppins_600SemiBold'},
  retryText: {color: '#A97627', fontFamily: 'Poppins_600SemiBold'}
});

export default OrderCartAssignment;
